import React, { useMemo } from 'react';
import { DndContext, closestCenter } from '@dnd-kit/core';
import { arrayMove, SortableContext, useSortable, rectSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import WidgetShell from '../../../../../ui/WidgetShell';
import { usePlanner } from '../../../contexts/PlannerContext';
import { SmallText } from '../../../../../ui/Typography.jsx';
import useMediaQuery from '../../../../../hooks/useMediaQuery.js';

const COLUMNS = [
  { id: 'backlog', label: 'Backlog' },
  { id: 'inProgress', label: 'In Progress' },
  { id: 'drying', label: 'Drying' },
  { id: 'bisque', label: 'Bisque' },
  { id: 'glaze', label: 'Glaze' },
  { id: 'complete', label: 'Complete' },
];

export default function KanbanView() {
  const { tasks = [], moveTaskToStatus } = usePlanner();
  const isMobile = useMediaQuery('(max-width: 640px)');

  const byStatus = useMemo(() => {
    return tasks.reduce((acc, t) => {
      const key = t?.status || 'backlog';
      acc[key] = acc[key] || [];
      acc[key].push(t);
      return acc;
    }, {});
  }, [tasks]);

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over) return;
    const taskId = active?.id;
    const targetCol = over?.data?.current?.columnId || over?.id;
    if (!taskId || !targetCol) return;

    const task = tasks.find((t) => t.id === taskId);
    if (!task || task.status === targetCol) return;
    moveTaskToStatus?.(task, targetCol);
  };

  return (
    <WidgetShell title="Kanban">
      <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <div className={isMobile ? 'flex gap-3 overflow-x-auto pb-2' : 'grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4'}>
          {COLUMNS.map((col) => (
            <KanbanColumn key={col.id} column={col} tasks={byStatus[col.id] || []} />
          ))}
        </div>
      </DndContext>
    </WidgetShell>
  );
}

function KanbanColumn({ column, tasks }) {
  return (
    <div className="rounded-2xl border p-3 border-[var(--mudo-border)] bg-[var(--mudo-bg-soft)]/85 backdrop-blur shadow-sm hover:shadow-md transition-shadow">
      <div className="text-sm font-semibold text-[var(--mudo-text)]">{column.label}</div>
      <SortableContext items={tasks.map((t) => t.id)} strategy={rectSortingStrategy}>
        <div className="mt-2 space-y-2">
          {tasks.map((t) => (
            <KanbanCard key={t.id} task={t} columnId={column.id} />
          ))}
        </div>
      </SortableContext>
    </div>
  );
}

function KanbanCard({ task, columnId }) {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({
    id: task.id,
    data: { columnId },
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="rounded-xl border p-3 border-[var(--mudo-border)] bg-[var(--mudo-bg-soft)]/90 backdrop-blur text-[var(--mudo-text)] shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all"
      {...attributes}
      {...listeners}
    >
      <div className="text-sm font-semibold">{task.title || 'Untitled task'}</div>
      <SmallText className="text-[var(--mudo-text-muted)]">{task.end || task.dueDate || 'No date'}</SmallText>
    </div>
  );
}
